import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Users, Plus, Trash2, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import StartFrameMenu from './StartFrameMenu';
import { getStudioVoices, type VoiceCatalog } from '../../services/studioService';
import { listSeriesCast, listCharacters, createCharacter, deleteCharacter, generateCharacterTurnaround, type Character } from '../../services/characterService';
import CastFromCrew from './CastFromCrew';
import { resolveDownloadUrl } from '../../services/storageService';

interface Props {
  /** The series this cast belongs to (null = a standalone video's characters). */
  seriesId: string | null;
  uid: string;
  conversationId: string | null;
  /** Notify the parent of the current cast (so shots can cast a character). */
  onCastChange?: (cast: Character[]) => void;
}

let cachedVoices: VoiceCatalog | null = null;

/** A small face thumbnail resolved from a storage path. */
const Face: React.FC<{ path: string | null; name: string }> = ({ path, name }) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => { let a = true; if (path) resolveDownloadUrl(path).then((u) => a && setUrl(u)).catch(() => {}); return () => { a = false; }; }, [path]);
  return url
    ? <img src={url} alt="" className="h-8 w-8 rounded-full object-cover" />
    : <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-900 text-[11px] font-semibold text-white dark:bg-white dark:text-secondary-900">{(name || '?').charAt(0).toUpperCase()}</span>;
};

/**
 * CastBible — the series' locked CHARACTERS (face + voice), so the same people look and sound the same across
 * EVERY episode (the continuity that makes multi-episode micro-drama coherent). Add a character (name + a locked
 * face + a voice), and cast them into shots (their face becomes the shot's start frame → consistent identity).
 */
const CastBible: React.FC<Props> = ({ seriesId, uid, conversationId, onCastChange }) => {
  const [expanded, setExpanded] = useState(false);
  /** Auto-expand ONCE on an empty cast: the one-tap crew rail is the fastest path to a consistent face, and a
   *  creator who never opens this section ships their first video with a different stranger in every shot. */
  const autoExpanded = useRef(false);
  const [cast, setCast] = useState<Character[]>([]);
  const [voices, setVoices] = useState<VoiceCatalog | null>(cachedVoices);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [facePath, setFacePath] = useState<string | null>(null);
  const [voiceId, setVoiceId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    const p = seriesId ? listSeriesCast(seriesId) : listCharacters().then((cs) => cs.filter((c) => !c.series_id));
    p.then((cs) => { setCast(cs); onCastChange?.(cs); }).catch(() => {});
  }, [seriesId, onCastChange]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!autoExpanded.current && cast.length === 0) { autoExpanded.current = true; setExpanded(true); }
  }, [cast.length]);
  useEffect(() => { if (!cachedVoices) getStudioVoices().then((v) => { cachedVoices = v; setVoices(v); }).catch(() => {}); }, []);

  const allVoices = voices ? [...voices.female, ...voices.male] : [];

  const save = async () => {
    if (!name.trim() || !facePath) { setError('A name and a face are required.'); return; }
    setSaving(true); setError('');
    try {
      const created = await createCharacter({ name: name.trim(), reference_path: facePath, series_id: seriesId, voice_id: voiceId || null });
      setName(''); setFacePath(null); setVoiceId(''); setAdding(false); load();
      // Build the turnaround in the background — the character is usable immediately off its front reference,
      // so we never make the creator wait on extra angles that only sharpen the NEXT render.
      void generateCharacterTurnaround(created.id).then(load).catch(() => {});
    } catch { setError('Could not save the character.'); }
    finally { setSaving(false); }
  };

  const remove = async (id: string) => {
    try { await deleteCharacter(id); setCast((prev) => { const next = prev.filter((c) => c.id !== id); onCastChange?.(next); return next; }); } catch { /* */ }
  };

  const voiceLabel = (id: string | null) => (id ? allVoices.find((v) => v.id === id)?.label || 'Voice' : 'No voice');

  return (
    <div className="mb-4 rounded-xl border border-secondary-200 dark:border-secondary-800 p-3">
      <button type="button" onClick={() => setExpanded((e) => !e)} className="flex w-full items-center gap-2 text-left">
        <Users className="h-4 w-4 text-secondary-500" />
        <span className="flex-1">
          <span className="block text-sm font-semibold text-secondary-900 dark:text-white">Cast{cast.length ? ` · ${cast.length}` : ''}</span>
          <span className="block text-[11px] text-secondary-400">Lock a face + voice so characters stay the same across episodes</span>
        </span>
        {expanded ? <ChevronDown className="h-4 w-4 text-secondary-400" /> : <ChevronRight className="h-4 w-4 text-secondary-400" />}
      </button>

      {expanded && (
        <div className="mt-3">
          {cast.map((c) => (
            <div key={c.id} className="mb-2 flex items-center gap-2.5 rounded-lg border border-secondary-200 dark:border-secondary-700 p-2">
              <Face path={c.reference_path} name={c.name} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-secondary-900 dark:text-white">{c.name}</span>
                <span className="block text-[11px] text-secondary-400">
                  {voiceLabel(c.voice_id)}
                  {/* The turnaround is the stronger identity lock — say so plainly, so the creator understands
                      why this character will hold their face better across shots. */}
                  {c.turnaround_paths?.length ? ' · Multi-angle lock' : ''}
                </span>
              </span>
              {/* The voice AUDITION button was removed 2026-08-23 (owner): performance now comes from the video
                  model itself, so a separate TTS sample here previews a voice that is not what the shot will
                  actually sound like — worse than showing nothing, because it sets a false expectation.
                  The voice NAME stays as the locked identity; `previewStudioVoice` is untouched and still
                  used by the per-line audition in DialogueEditor. */}
              <button type="button" onClick={() => remove(c.id)} aria-label={`Remove ${c.name}`} className="p-1 text-secondary-400 hover:text-secondary-800 dark:hover:text-white"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}

          {adding ? (
            <div className="rounded-lg border border-secondary-200 dark:border-secondary-700 p-2.5">
              <div className="flex items-start gap-2.5">
                <StartFrameMenu framePath={facePath} uid={uid} conversationId={conversationId} onFrame={setFacePath} />
                <div className="flex-1 space-y-1.5">
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Character name" className="w-full rounded-md border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-2 py-1 text-sm text-secondary-900 dark:text-white placeholder:text-secondary-400" />
                  <div className="flex items-center gap-1.5">
                    <select value={voiceId} onChange={(e) => setVoiceId(e.target.value)} className="flex-1 rounded-md border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-2 py-1 text-[11px] text-secondary-700 dark:text-secondary-200">
                      <option value="">Voice…</option>
                      {voices && <><optgroup label="Female">{voices.female.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}</optgroup><optgroup label="Male">{voices.male.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}</optgroup></>}
                    </select>

                  </div>
                </div>
              </div>
              <div className="mt-2 flex justify-end gap-1.5">
                <button type="button" onClick={() => { setAdding(false); setName(''); setFacePath(null); setVoiceId(''); }} className="rounded-lg px-2.5 py-1 text-xs text-secondary-500 hover:text-secondary-800 dark:hover:text-white">Cancel</button>
                <button type="button" onClick={save} disabled={saving || !name.trim() || !facePath} className="inline-flex items-center gap-1 rounded-lg bg-secondary-900 px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50 dark:bg-white dark:text-secondary-900">{saving ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Add to cast</button>
              </div>
            </div>
          ) : (
            <>
              {/* Cast a crew specialist in one tap — the fastest path off an empty cast list (the biggest
                  drag on time-to-first-video). Building an original character stays available below. */}
              <CastFromCrew
                seriesId={seriesId}
                castNames={cast.map((c) => c.name)}
                onCast={(c) => {
                  setCast((prev) => { const next = [...prev, c]; onCastChange?.(next); return next; });
                  void generateCharacterTurnaround(c.id).then(load).catch(() => {});
                }}
              />
              <button type="button" onClick={() => setAdding(true)} className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-secondary-300 dark:border-secondary-600 py-2 text-xs text-secondary-500 hover:border-secondary-400">
                <Plus className="h-3.5 w-3.5" /> Create your own character
              </button>
            </>
          )}
          {error && <p className="mt-2 text-[11px] text-secondary-500 dark:text-secondary-400">{error}</p>}
        </div>
      )}
    </div>
  );
};

export default CastBible;
